import React, { useState } from "react";
import { Link, useNavigate, Outlet, useLocation } from "react-router-dom";
import {
  BsRobot,
  BsPeople,
  BsGear,
  BsList,
  BsX,
  BsBoxArrowRight,
  BsPersonCircle,
  BsChevronDown,
  BsChevronRight,
  BsFileText,
} from "react-icons/bs";
import { useAuth } from "../context/AuthContext";
import {
  CheckCheck,
  FileText,
  FileUser,
  SquareUser,
  UserPlus,
  UserRoundPlus,
} from "lucide-react";

const Layout = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState({});
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const isActive = (path) => {
    return location.pathname === path;
  };

  const isParentActive = (parentPath, subPaths) => {
    return subPaths.some((subPath) => location.pathname === subPath);
  };

  const getLinkClasses = (path) => {
    const baseClasses =
      "flex items-center px-4 py-3 rounded-lg transition-colors";
    const activeClasses = "bg-indigo-50 text-indigo-700 font-medium";
    const inactiveClasses =
      "text-gray-700 hover:bg-indigo-50 hover:text-indigo-700";

    return `${baseClasses} ${isActive(path) ? activeClasses : inactiveClasses}`;
  };

  const getParentLinkClasses = (subPaths) => {
    const baseClasses =
      "flex items-center justify-between px-4 py-3 rounded-lg transition-colors cursor-pointer";
    const activeClasses = "bg-indigo-50 text-indigo-700 font-medium";
    const inactiveClasses =
      "text-gray-700 hover:bg-indigo-50 hover:text-indigo-700";

    return `${baseClasses} ${
      isParentActive("", subPaths) ? activeClasses : inactiveClasses
    }`;
  };

  const getSubLinkClasses = (path) => {
    const baseClasses =
      "flex items-center px-8 py-2 rounded-lg transition-colors text-sm";
    const activeClasses = "bg-indigo-100 text-indigo-700 font-medium";
    const inactiveClasses =
      "text-gray-600 hover:bg-indigo-50 hover:text-indigo-600";

    return `${baseClasses} ${isActive(path) ? activeClasses : inactiveClasses}`;
  };

  const toggleMenu = (menuKey) => {
    setExpandedMenus((prev) => ({
      ...prev,
      [menuKey]: !prev[menuKey],
    }));
  };

  const handleLogout = () => {
    logout();
    setShowUserMenu(false);
    navigate("/login");
  };

  const handleSidebarClose = () => {
    setIsSidebarOpen(false);
  };

  return (
    <div className="flex flex-col md:flex-row h-screen bg-gray-50">
      {/* Mobile Header */}
      <div className="md:hidden bg-white shadow-sm px-4 flex justify-between items-center">
        <div className="flex items-center justify-between w-full">
          <span className="flex items-center">
            <button
              onClick={() => setIsSidebarOpen(!isSidebarOpen)}
              className="mr-4 text-gray-600 hover:text-gray-900"
            >
              {isSidebarOpen ? <BsX size={24} /> : <BsList size={24} />}
            </button>
            {/* <h1 className="text-xl font-bold text-indigo-700">Talent Repo</h1> */}
            <img src={`${process.env.PUBLIC_URL}/images/kajaria-logo.png`} alt="Kajaria" />
          </span>
          <div className="flex items-center mr-4">
            {/* logos */}
            <div className="flex items-center space-x-6 my-4">
              <img
                src={`${process.env.PUBLIC_URL}/images/kajaria-logo.png`}
                alt="Logo 1"
                className="filter dark-blue h-4"
              />
              <div>
                <img
                  src={`${process.env.PUBLIC_URL}/images/gresbond-logo.png`}
                  alt="Gresbond"
                  className="filter dark-blue h-4 mb-1"
                />
                <img
                  src={`${process.env.PUBLIC_URL}/images/kerovit_logo.png`}
                  alt="Kerovit"
                  className="filter dark-blue h-3"
                />
              </div>
            </div>
          </div>
        </div>
        {user && (
          <div className="relative">
            <button
              onClick={() => setShowUserMenu(!showUserMenu)}
              className="flex items-center text-sm rounded-full focus:outline-none"
            >
              <span className="sr-only">Open user menu</span>
              <div className="h-8 w-8 rounded-full bg-indigo-100 flex items-center justify-center">
                <BsPersonCircle className="h-6 w-6 text-indigo-600" />
              </div>
            </button>
            {showUserMenu && (
              <div className="origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10">
                <div className="py-1" role="menu" aria-orientation="vertical">
                  <div className="px-4 py-2 text-sm text-gray-700 border-b">
                    <div className="font-medium">{user.name || user.email}</div>
                    <div className="text-xs text-gray-500">
                      {user.role || "User"}
                    </div>
                  </div>
                  <button
                    onClick={handleLogout}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                    role="menuitem"
                  >
                    <BsBoxArrowRight className="mr-2" />
                    Sign out
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Sidebar */}
      <div
        className={`${isSidebarOpen ? "translate-x-0" : "-translate-x-full"} 
          md:translate-x-0 fixed md:static inset-y-0 left-0 w-64 bg-white shadow-lg md:shadow-none 
          transform transition-transform duration-200 ease-in-out z-50`}
      >
        <div className="p-5 md:block border-b-2 border-gray-300">
          <h1 className="text-2xl font-bold text-indigo-700 flex items-center">
            <BsRobot className="mx-2" />
            <img src={`${process.env.PUBLIC_URL}/images/kajaria-logo.png`} alt="Kajaria" className="filter dark-blue h-8" />
          </h1>
        </div>
        <nav className="mt-2 md:mt-4">
          <div className="px-2 space-y-1 md:space-y-2">
            {user.is_superuser&&<><Link
              to="/"
              onClick={handleSidebarClose}
              className={getLinkClasses("/")}
            >
              <BsRobot className="mr-3" />
              Dashboard
            </Link>
            <div>
              <div
                onClick={() => toggleMenu("checklist")}
                className={getParentLinkClasses([
                  "/checklist",
                  "/checklist/inactive",
                ])}
              >
                <div className="flex items-center">
                  <BsFileText className="mr-3" />
                  Documents checklist
                </div>
                {expandedMenus.checklist ? (
                  <BsChevronDown className="h-4 w-4" />
                ) : (
                  <BsChevronRight className="h-4 w-4" />
                )}
              </div>

              {/* Sub-menu items */}
              {expandedMenus.checklist && (
                <div className="mt-1 space-y-1">
                  <Link
                    to="/checklist"
                    onClick={handleSidebarClose}
                    className={getSubLinkClasses("/checklist")}
                  >
                    <FileUser className="mr-3" />
                    Active documents
                  </Link>
                  <Link
                    to="/checklist/inactive"
                    onClick={handleSidebarClose}
                    className={getSubLinkClasses("/checklist/inactive")}
                  >
                    <FileUser className="mr-3" />
                    Inactive documents
                  </Link>
                </div>
              )}
            </div>
            <div>
              <div
                onClick={() => toggleMenu("employees")}
                className={getParentLinkClasses([
                  "/employees",
                  "/employees/inactive",
                ])}
              >
                <div className="flex items-center">
                  <BsPeople className="mr-3" />
                  Employees Directory
                </div>
                {expandedMenus.employees ? (
                  <BsChevronDown className="h-4 w-4" />
                ) : (
                  <BsChevronRight className="h-4 w-4" />
                )}
              </div>

              {/* Sub-menu items */}
              {expandedMenus.employees && (
                <div className="mt-1 space-y-1">
                  <Link
                    to="/employees"
                    onClick={handleSidebarClose}
                    className={getSubLinkClasses("/employees")}
                  >
                    <SquareUser className="mr-3" />
                    Active Employees
                  </Link>
                  <Link
                    to="/employees/inactive"
                    onClick={handleSidebarClose}
                    className={getSubLinkClasses("/employees/inactive")}
                  >
                    <SquareUser className="mr-3" />
                    Inactive Employees
                  </Link>
                </div>
              )}
            </div></>}
            <Link
              to="/onboarding"
              onClick={handleSidebarClose}
              className={getLinkClasses("/onboarding")}
            >
              <UserRoundPlus className="mr-3" />
              Onboarding
            </Link>
            {/* <Link
              to="/eforms"
              onClick={handleSidebarClose}
              className={getLinkClasses('/eforms')}
            >
              <FileText className="mr-3" />
              Forms
            </Link> */}
            {/* <Link
              to="/hr-onboarding-panel"
              onClick={handleSidebarClose}
              className={getLinkClasses('/hr-onboarding-panel')}
            >
              <UserPlus className="mr-3"/>
              HR Onboarding Panel
            </Link> */}
            {/* <Link
              to="/approve-updates"
              onClick={handleSidebarClose}
              className={getLinkClasses('/approve-updates')}
            >
              <CheckCheck className="mr-3"/>
              Approve Updates Request
            </Link> */}
          </div>
        </nav>
      </div>

      {/* Overlay for mobile */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-10 md:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <div className="flex-1 overflow-auto">
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Desktop Header */}
          <header className="hidden md:flex items-center justify-between bg-white shadow-sm p-4">
            <div className="flex-1">
              <div className="flex items-center space-x-6">
                {/* <img
                  src="images/kajaria-logo.png"
                  alt="Logo 1"
                  className="filter dark-blue h-10"
                /> */}
                <img
                  src="images/kerovit_logo.png"
                  alt="Kerovit"
                  className="filter dark-blue h-10"
                />
                <img
                  src="images/gresbond-logo.png"
                  alt="Gresbond"
                  className="filter dark-blue h-10"
                />
              </div>
            </div>

            {user && (
              <div className="relative">
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="flex items-center text-sm rounded-full focus:outline-none"
                >
                  <span className="sr-only">Open user menu</span>
                  <div className="h-8 w-8 rounded-full bg-indigo-100 flex items-center justify-center">
                    <BsPersonCircle className="h-6 w-6 text-indigo-600" />
                  </div>
                  <span className="ml-2 text-gray-700">
                    {user.name || user.email}
                  </span>
                  <svg
                    className="ml-1 h-4 w-4 text-gray-500"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
                {showUserMenu && (
                  <div className="origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10">
                    <div
                      className="py-1"
                      role="menu"
                      aria-orientation="vertical"
                    >
                      <div className="px-4 py-2 text-sm text-gray-700 border-b">
                        <div className="font-medium">
                          {user.name || user.email}
                        </div>
                        <div className="text-xs text-gray-500">
                          {user.role || "User"}
                        </div>
                      </div>
                      <button
                        onClick={handleLogout}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                        role="menuitem"
                      >
                        <BsBoxArrowRight className="mr-2" />
                        Sign out
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </header>
          <main className="flex-1 overflow-y-auto p-4 md:p-8">
            <Outlet />
          </main>
        </div>
      </div>
    </div>
  );
};

export default Layout;
